import { Col, Descriptions, Row, Tag } from 'antd';
import { Button, Card, Divider, Spin, Table } from 'pink-lava-ui';
import { ChevronDown } from 'lucide-react';
import { BookingTypeLabel, type Booking, type BookingShipment } from '@dc-trans/data-layer';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useUrlParamsContext } from '@/context/urlParamsContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import BookingStatusTag from './BookingStatusTag';
import { useState } from 'react';
import { useBookingShipmentInfinite } from '@/queries';
import dayjs from 'dayjs';
import type { ColumnType } from 'antd/lib/table';
import type { SortOrder } from 'antd/es/table/interface';

interface ShipmentData {
  tms_ref_number: string;
  origin_name: string;
  destination_name: string;
}

interface BookingCardProps {
  booking: Booking;
  onDetailClick: () => void;
}

const BookingCard = ({ booking, onDetailClick }: BookingCardProps) => {
  const [showShipmentsExpanded, setShowShipmentsExpanded] = useState(false);
  const [sortConfig, setSortConfig] = useState<{ sortBy: string; sortOrder?: SortOrder }>({
    sortBy: '',
    sortOrder: undefined,
  });
  const { isMobile, isLgAndUp, isXlAndUp, isMdAndUp } = useBreakpoint();
  const { params } = useUrlParamsContext();

  const {
    data: shipmentData,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useBookingShipmentInfinite(
    {
      limit: 5,
      filters: {
        booking_ulid: booking.ulid,
        tms_ref_number: params?.ship_ref_number || '',
        do_number: params?.do_number || '',
        dest_ulid: params?.dest_ulid || [],
        origin_ulid: params?.origin_ulid || [],
        sort_by: sortConfig.sortBy,
        sort_order: sortConfig.sortOrder === 'ascend' ? 'asc' : 'desc',
      },
    },
    showShipmentsExpanded
  );

  const handleSort = (column: string) => {
    setSortConfig(prevSort => ({
      sortBy: column,
      sortOrder: prevSort.sortBy === column && prevSort.sortOrder === 'ascend' ? 'descend' : 'ascend',
    }));
  };

  const tableColumns: ColumnType<BookingShipment>[] = [
    {
      title: 'Nomor Shipment',
      dataIndex: 'tms_ref_number',
      key: 'tms_ref_number',
      sorter: true,
      sortOrder: sortConfig.sortBy === 'tms_ref_number' ? sortConfig.sortOrder : undefined,
      onHeaderCell: () => ({
        onClick: () => handleSort('tms_ref_number'),
        style: { cursor: 'pointer' },
      }),
    },
    {
      title: 'Nomor Delivery Order',
      dataIndex: 'do_number',
      key: 'do_number',
      sorter: true,
      sortOrder: sortConfig.sortBy === 'do_number' ? sortConfig.sortOrder : undefined,
      onHeaderCell: () => ({
        onClick: () => handleSort('do_number'),
        style: { cursor: 'pointer' },
      }),
    },
    {
      title: 'Asal Pengiriman',
      dataIndex: 'origin_name',
      key: 'origin_name',
    },
    {
      title: 'Tujuan Pengiriman',
      dataIndex: 'destination_name',
      key: 'destination_name',
    },
  ];

  const flattenShipmentData = shipmentData?.pages?.flatMap(page => page.data.items) || [];

  return (
    <Card
      rounded="large"
      size={isMobile ? 'small' : 'default'}
      className={booking.vehicle?.license_plate ? 'border-2 border-white' : 'border-red-500 border-2'}
    >
      <Row gutter={[16, 16]} className="flex items-end justify-end">
        <Col span={24}>
          <div className="flex flex-row items-center">
            <div className="text-lg w-full flex-1">{booking.tms_booking}</div>
            <BookingStatusTag status={booking.status} />
          </div>
        </Col>
        <Col flex={24} className="flex flex-row gap-6 items-end">
          <Descriptions
            colon={false}
            size="small"
            column={isXlAndUp ? 3 : isMdAndUp ? 2 : 1}
            layout={isMdAndUp ? 'vertical' : 'horizontal'}
            classNames={{
              root: 'flex-1',
              label: 'font-nunito text-gray-400 text-xs',
              content: 'font-nunito flex-1 text-xs text-right justify-end md:text-left md:justify-start',
            }}
            items={[
              {
                label: 'Tipe Booking',
                children: BookingTypeLabel[booking.type as keyof typeof BookingTypeLabel],
              },
              isMdAndUp
                ? {
                    label: 'Req. Shipping Window',
                    children: booking.req_ship_window
                      ? `${booking.req_ship_window.incoming} - ${booking.req_ship_window.outgoing}`
                      : '',
                  }
                : {
                    label: 'Vendor',
                    children: booking.vendor.name,
                  },
              {
                label: 'Nomor Kendaraan',
                children: booking.vehicle.license_plate ? (
                  booking.vehicle.license_plate
                ) : (
                  <Tag color="error" bordered={false} className="font-nunito!">
                    Belum Ditugaskan
                  </Tag>
                ),
              },
              {
                label: 'Req. Plant Delivery Date',
                children: booking.req_delivery_date ? dayjs(booking.req_delivery_date).format('DD MMM YYYY') : '',
              },
              isMdAndUp
                ? {
                    label: 'Vendor',
                    children: booking.vendor.name,
                  }
                : {
                    label: 'Req. Shipping Window',
                    children: booking.req_ship_window
                      ? `${booking.req_ship_window.incoming} - ${booking.req_ship_window.outgoing}`
                      : '',
                  },
              {
                label: 'Req. Jenis Kendaraan',
                children: booking.req_vehicle_type ? booking.req_vehicle_type.name : '',
              },
            ]}
          />
          {isLgAndUp && (
            <>
              <Button shape="round" type="secondary" size="small" onClick={onDetailClick}>
                Lihat Detail
              </Button>
            </>
          )}
        </Col>
      </Row>
      <Divider className="my-4!" />
      {showShipmentsExpanded && (
        <InfiniteScroll
          dataLength={flattenShipmentData.length}
          next={fetchNextPage}
          hasMore={hasNextPage}
          loader={
            <div className="my-4">
              <Spin size={'large'} />
            </div>
          }
          height={flattenShipmentData.length >= 5 ? 39 * 5 + 40 : 39 * flattenShipmentData.length + 40}
          className={`my-4`}
        >
          <Table
            size="small"
            loading={isLoading}
            columns={tableColumns as any}
            dataSource={flattenShipmentData || []}
            pagination={false}
            rowKey="ulid"
          />
        </InfiniteScroll>
      )}
      <div className="flex flex-row items-center pb-4 justify-end lg:hidden">
        <Button shape="round" type="secondary" size="small" block onClick={onDetailClick}>
          Lihat Detail
        </Button>
      </div>
      <div className="flex justify-between">
        <div />
        <Button
          size="small"
          type="ghost"
          className="px-1!"
          onClick={() => setShowShipmentsExpanded(!showShipmentsExpanded)}
        >
          {showShipmentsExpanded ? 'Sembunyikan' : 'Tampilkan Shipment'}
          <ChevronDown className={showShipmentsExpanded ? 'rotate-180' : ''} />
        </Button>
      </div>
    </Card>
  );
};

export default BookingCard;
export type { BookingCardProps, ShipmentData };
